import React, { useRef, useContext, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  ArrowRight, ArrowUpRight, BarChart2, Bookmark, Briefcase, Building, CheckCircle, 
  ChevronRight, Clock, Compass, Download, Eye, Lightbulb, Mail, MessageSquare, 
  RefreshCw, Share2, Sparkles, Target, TrendingUp, Users, Zap, BookOpen, Menu, X 
} from 'lucide-react';
import Header from './components/Header';
import Footer from './components/Footer';
import LeadForm from './components/LeadForm';
import SearchResults from './components/SearchResults';
import { COLORS, getColor, getTimeHorizonStyle, getDisruptionColor } from './utils/colors';
import { getTrendUrl } from './utils/slugs';
import { SearchContext } from './App';

// Signal component for trend detail
const SignalCard = ({ signal }) => {
  const disruptionColor = getDisruptionColor(signal.disruptionLevel);
  
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
      <div className="flex justify-between items-start mb-3">
        <h4 className="font-semibold text-gray-900">{signal.signal}</h4>
        <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${disruptionColor}`}>
          {signal.disruptionLevel}
        </span>
      </div>
      <p className="text-gray-600 text-sm">{signal.description}</p>
    </div>
  );
};

// Example card for trend detail
const ExampleCard = ({ example, type }) => {
  const bgColor = type === 'startup' ? 'bg-red-50' : 'bg-amber-50';
  const borderColor = type === 'startup' ? 'border-red-100' : 'border-amber-100';
  const textColor = type === 'startup' ? 'text-red-900' : 'text-amber-900';
  
  return (
    <div className={`${bgColor} ${borderColor} border rounded-lg p-4`}>
      <h4 className={`font-semibold ${textColor} mb-1`}>{example.name}</h4>
      <p className="text-gray-600 text-sm">{example.description}</p>
    </div>
  );
};

// Scenario card for trend detail
const ScenarioCard = ({ scenario, index }) => {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center text-red-800 font-bold">
          {String.fromCharCode(65 + index)}
        </div>
        <h4 className="font-semibold text-gray-900">{scenario.title}</h4>
      </div>
      <p className="text-gray-600">{scenario.description}</p>
    </div>
  );
};

// Enhanced trend detail page with continuous scrolling design
const TrendDetailPage = ({ trend, relatedTrends }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const colorStyle = getColor(trend.color);
  const { searchQuery } = useContext(SearchContext) || {};
  const [mobileNavOpen, setMobileNavOpen] = React.useState(false);
  
  // For smooth scrolling to sections
  const sectionRefs = {
    overview: useRef(null),
    drivers: useRef(null),
    signals: useRef(null),
    shifts: useRef(null),
    examples: useRef(null),
    scenarios: useRef(null),
    implications: useRef(null)
  };
  
  // Scroll to top when navigating to this page
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  
  // Handle back navigation
  const handleBack = () => {
    navigate('/');
  };
  
  // Handle trend selection
  const handleSelectTrend = (trendId) => {
    const selectedTrend = relatedTrends.find(t => t.id === trendId);
    if (selectedTrend) {
      navigate(getTrendUrl(selectedTrend));
    }
  };
  
  const scrollToSection = (sectionId) => {
    sectionRefs[sectionId]?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setMobileNavOpen(false); // Close mobile nav when selecting a section
  };
  
  // Share functionality
  const handleShareTrend = () => {
    if (navigator.share) {
      // Use native sharing on mobile if available
      navigator.share({
        title: trend.title,
        text: trend.subtitle || trend.summary.substring(0, 100) + '...',
        url: window.location.href,
      }).catch(error => {
        console.log('Error sharing:', error);
        // Fallback
        navigator.clipboard.writeText(window.location.href);
        alert('Link copied to clipboard!');
      });
    } else {
      // For desktop
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };
  
  return (
    <div className="min-h-screen bg-white">
      <Header onBack={handleBack} />
      
      {/* Hero Section */}
      <section className="pt-20 sm:pt-24 md:pt-28 pb-10 sm:pb-16 text-white relative overflow-hidden" style={{backgroundColor: COLORS.primary}}>
        {/* Background patterns */}
        <div className="absolute inset-0 opacity-10">
          <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
            <defs>
              <pattern id="smallGrid" width="8" height="8" patternUnits="userSpaceOnUse">
                <path d="M 8 0 L 0 0 0 8" fill="none" stroke="white" strokeWidth="0.5" />
              </pattern>
              <pattern id="grid" width="80" height="80" patternUnits="userSpaceOnUse">
                <rect width="80" height="80" fill="url(#smallGrid)" />
                <path d="M 80 0 L 0 0 0 80" fill="none" stroke="white" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          <div className="max-w-4xl">
            <div className="flex flex-wrap gap-2 sm:gap-3 mb-4 sm:mb-8">
              <div className="inline-flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 bg-white/20 backdrop-blur-sm rounded-full">
                <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                <span className="text-xs sm:text-sm font-medium text-white">{trend.category}</span>
              </div>
              
              <div className="inline-flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 bg-white/20 backdrop-blur-sm rounded-full">
                <Clock className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                <span className="text-xs sm:text-sm font-medium text-white">{trend.timeHorizon}</span>
              </div>
            </div>
            
            <h1 className="text-xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 sm:mb-6 leading-tight">{trend.title}</h1>
            {trend.subtitle && (
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-white/80 mb-4 sm:mb-8">{trend.subtitle}</p>
            )}
            
            <div className="flex flex-wrap gap-2 sm:gap-4 text-xs sm:text-sm">
              <div className="bg-white/20 backdrop-blur-sm px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg">
                <div className="flex items-center gap-1 sm:gap-2">
                  <BarChart2 className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                  <span>Maturity: {trend.maturity}%</span>
                </div>
              </div>
              
              <div className="bg-white/20 backdrop-blur-sm px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg">
                <div className="flex items-center gap-1 sm:gap-2">
                  <Zap className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                  <span>Focus: {trend.focusArea}</span>
                </div>
              </div>
              
              <button 
                onClick={handleShareTrend}
                className="bg-white/20 backdrop-blur-sm px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg flex items-center gap-1 sm:gap-2 cursor-pointer hover:bg-white/30 transition-colors"
                aria-label="Share this trend"
              >
                <Share2 className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                <span>Share</span>
              </button>
              
              <button
                className="bg-white/20 backdrop-blur-sm px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg flex items-center gap-1 sm:gap-2 cursor-pointer hover:bg-white/30 transition-colors"
                aria-label="Save this trend"
              >
                <Bookmark className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                <span>Save</span>
              </button>
            </div>
          </div>
        </div>
      </section>
      
      {/* Search Results Overlay */}
      {searchQuery && (
        <div className="pt-4 pb-8 bg-gray-50 border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <SearchResults showFilters={false} />
          </div>
        </div>
      )}
      
      {/* Quick Navigation - Desktop */}
      <div className="sticky top-16 md:top-20 bg-white border-b border-gray-200 z-30 shadow-sm hidden md:block">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex overflow-x-auto py-1 hide-scrollbar -mx-4 px-4 sm:mx-0 sm:px-0">
            <button 
              onClick={() => scrollToSection('overview')}
              className="flex items-center gap-1 px-3 sm:px-4 py-3 text-sm font-medium text-gray-600 hover:text-red-600 whitespace-nowrap transition-colors"
            >
              <Eye className="w-4 h-4" />
              <span>Overview</span>
            </button>
            {trend.drivers && (
              <button 
                onClick={() => scrollToSection('drivers')}
                className="flex items-center gap-1 px-3 sm:px-4 py-3 text-sm font-medium text-gray-600 hover:text-red-600 whitespace-nowrap transition-colors"
              >
                <TrendingUp className="w-4 h-4" />
                <span>Drivers</span>
              </button>
            )}
            {trend.drivers?.microSignals && (
              <button 
                onClick={() => scrollToSection('signals')}
                className="flex items-center gap-1 px-3 sm:px-4 py-3 text-sm font-medium text-gray-600 hover:text-red-600 whitespace-nowrap transition-colors"
              >
                <Zap className="w-4 h-4" />
                <span>Signals</span>
              </button>
            )}
            {trend.shifts && (
              <button 
                onClick={() => scrollToSection('shifts')}
                className="flex items-center gap-1 px-3 sm:px-4 py-3 text-sm font-medium text-gray-600 hover:text-red-600 whitespace-nowrap transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Shifts</span>
              </button>
            )}
            {trend.examples && (
              <button 
                onClick={() => scrollToSection('examples')}
                className="flex items-center gap-1 px-3 sm:px-4 py-3 text-sm font-medium text-gray-600 hover:text-red-600 whitespace-nowrap transition-colors"
              >
                <Briefcase className="w-4 h-4" />
                <span>Examples</span>
              </button>
            )}
            {trend.futureScenarios && (
              <button 
                onClick={() => scrollToSection('scenarios')}
                className="flex items-center gap-1 px-3 sm:px-4 py-3 text-sm font-medium text-gray-600 hover:text-red-600 whitespace-nowrap transition-colors"
              >
                <Compass className="w-4 h-4" />
                <span>Scenarios</span>
              </button>
            )}
            {trend.strategicImplications && (
              <button 
                onClick={() => scrollToSection('implications')}
                className="flex items-center gap-1 px-3 sm:px-4 py-3 text-sm font-medium text-gray-600 hover:text-red-600 whitespace-nowrap transition-colors"
              >
                <Target className="w-4 h-4" />
                <span>Implications</span>
              </button>
            )}
          </div>
        </div>
      </div>
      
      {/* Mobile Navigation Toggle */}
      <div className="sticky top-16 bg-white border-b border-gray-200 z-30 shadow-sm md:hidden py-2">
        <div className="max-w-7xl mx-auto px-4 flex justify-between items-center">
          <button 
            onClick={() => setMobileNavOpen(!mobileNavOpen)}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 rounded-lg border border-gray-200"
          >
            {mobileNavOpen ? (
              <>
                <X className="w-4 h-4" /> 
                <span>Close Menu</span>
              </>
            ) : (
              <>
                <Menu className="w-4 h-4" /> 
                <span>Sections</span>
              </>
            )}
          </button>
          
          <div className="text-sm text-gray-500 truncate">
            {trend.title.substring(0, 25)}{trend.title.length > 25 ? '...' : ''}
          </div>
        </div>
        
        {/* Mobile Navigation Dropdown */}
        {mobileNavOpen && (
          <div className="absolute top-full left-0 right-0 bg-white border-b border-gray-200 shadow-md z-40 px-4 py-2 max-h-[50vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-2">
              <button 
                onClick={() => scrollToSection('overview')}
                className="flex items-center gap-2 p-3 text-sm text-gray-700 hover:bg-gray-50 rounded-lg border border-gray-100"
              >
                <Eye className="w-4 h-4 text-red-600" />
                <span>Overview</span>
              </button>
              
              {trend.drivers && (
                <button 
                  onClick={() => scrollToSection('drivers')}
                  className="flex items-center gap-2 p-3 text-sm text-gray-700 hover:bg-gray-50 rounded-lg border border-gray-100"
                >
                  <TrendingUp className="w-4 h-4 text-red-600" />
                  <span>Drivers</span>
                </button>
              )}
              
              {trend.drivers?.microSignals && (
                <button 
                  onClick={() => scrollToSection('signals')}
                  className="flex items-center gap-2 p-3 text-sm text-gray-700 hover:bg-gray-50 rounded-lg border border-gray-100"
                >
                  <Zap className="w-4 h-4 text-red-600" />
                  <span>Signals</span>
                </button>
              )}
              
              {trend.shifts && (
                <button 
                  onClick={() => scrollToSection('shifts')}
                  className="flex items-center gap-2 p-3 text-sm text-gray-700 hover:bg-gray-50 rounded-lg border border-gray-100"
                >
                  <RefreshCw className="w-4 h-4 text-red-600" />
                  <span>Shifts</span>
                </button>
              )}
              
              {trend.examples && (
                <button 
                  onClick={() => scrollToSection('examples')}
                  className="flex items-center gap-2 p-3 text-sm text-gray-700 hover:bg-gray-50 rounded-lg border border-gray-100"
                >
                  <Briefcase className="w-4 h-4 text-red-600" />
                  <span>Examples</span>
                </button>
              )}
              
              {trend.futureScenarios && (
                <button 
                  onClick={() => scrollToSection('scenarios')}
                  className="flex items-center gap-2 p-3 text-sm text-gray-700 hover:bg-gray-50 rounded-lg border border-gray-100"
                >
                  <Compass className="w-4 h-4 text-red-600" />
                  <span>Scenarios</span>
                </button>
              )}
              
              {trend.strategicImplications && (
                <button 
                  onClick={() => scrollToSection('implications')}
                  className="flex items-center gap-2 p-3 text-sm text-gray-700 hover:bg-gray-50 rounded-lg border border-gray-100"
                >
                  <Target className="w-4 h-4 text-red-600" />
                  <span>Implications</span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 xl:gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8 sm:space-y-12 md:space-y-16">
            {/* Overview Section */}
            <section ref={sectionRefs.overview} className="scroll-mt-24">
              <div className="space-y-6 sm:space-y-8">
                {/* Summary Card */}
                <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 mb-6 sm:mb-8 shadow-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-white" style={{backgroundColor: COLORS.primary}}>
                      <Eye className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                    </div>
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Summary</h2>
                  </div>
                  
                  <p className="text-gray-800 text-base sm:text-lg mb-4 sm:mb-6 leading-relaxed">{trend.summary}</p>
                  
                  <div className="rounded-xl p-4 sm:p-6 border" style={{backgroundColor: `${COLORS.primary}10`, borderColor: `${COLORS.primary}30`}}>
                    <h3 className="font-bold text-gray-900 mb-2 sm:mb-3 flex items-center gap-2">
                      <Lightbulb className="w-4 h-4 sm:w-5 sm:h-5 text-amber-500" />
                      <span>The Opportunity</span>
                    </h3>
                    <p className="text-gray-800 text-sm sm:text-base">{trend.opportunity}</p>
                  </div>
                </div>
                
                {/* Background Card if available */}
                {trend.background && (
                  <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-white" style={{backgroundColor: COLORS.primary}}>
                        <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                      </div>
                      <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Background</h2>
                    </div>
                    
                    <p className="text-gray-800 text-sm sm:text-base mb-4 sm:mb-6 leading-relaxed">{trend.background.context}</p>
                    
                    <h3 className="font-bold text-gray-900 mb-2 sm:mb-3">Key Questions</h3>
                    <ul className="space-y-2 sm:space-y-3">
                      {trend.background.initialQuestions.map((question, idx) => (
                        <li key={idx} className="flex items-start gap-2 sm:gap-3">
                          <div className="p-1 rounded-full mt-1" style={{backgroundColor: `${COLORS.primary}15`}}>
                            <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" style={{color: COLORS.primary}} />
                          </div>
                          <p className="text-gray-700 text-sm sm:text-base">{question}</p>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </section>
            
            {/* Drivers Section if available */}
            {trend.drivers && (
              <section ref={sectionRefs.drivers} className="scroll-mt-24">
                <div className="space-y-6 sm:space-y-8">
                  <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-4 sm:mb-6">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-white" style={{backgroundColor: COLORS.primary}}>
                        <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                      </div>
                      <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Macro Drivers</h2>
                    </div>
                    
                    <div className="space-y-3 sm:space-y-4">
                      {trend.drivers.macroDrivers.map((driver, idx) => (
                        <div key={idx} className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 rounded-lg border" style={{backgroundColor: `${COLORS.primary}10`, borderColor: `${COLORS.primary}30`}}>
                          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-white" style={{backgroundColor: COLORS.primary}}>
                            <span className="text-white text-sm sm:text-base font-bold">{idx + 1}</span>
                          </div>
                          <p className="font-medium text-gray-800 text-sm sm:text-base">{driver}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </section>
            )}
            
            {/* Signals Section if available */}
            {trend.drivers?.microSignals && (
              <section ref={sectionRefs.signals} className="scroll-mt-24">
                <div className="space-y-6 sm:space-y-8">
                  <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-4 sm:mb-6">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-white" style={{backgroundColor: COLORS.primary}}>
                        <Zap className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                      </div>
                      <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Micro Signals</h2>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      {trend.drivers.microSignals.map((signal, idx) => (
                        <SignalCard key={idx} signal={signal} />
                      ))}
                    </div>
                  </div>
                </div>
              </section>
            )}
            
            {/* Shifts Section if available */}
            {trend.shifts && (
              <section ref={sectionRefs.shifts} className="scroll-mt-24">
                <div className="space-y-6 sm:space-y-8">
                  <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-4 sm:mb-6">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-white" style={{backgroundColor: COLORS.primary}}>
                        <RefreshCw className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                      </div>
                      <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Key Shifts</h2>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h3 className="font-bold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
                          <Users className="w-4 h-4 sm:w-5 sm:h-5" style={{color: COLORS.primary}} />
                          <span>Consumer Behavior Shifts</span>
                        </h3>
                        <div className="space-y-2 sm:space-y-3">
                          {trend.shifts.consumerBehavior.map((shift, idx) => (
                            <div key={idx} className="flex items-start gap-2 sm:gap-3">
                              <div className="p-1 rounded-full mt-1" style={{backgroundColor: `${COLORS.primary}15`}}>
                                <ArrowUpRight className="w-3 h-3 sm:w-4 sm:h-4" style={{color: COLORS.primary}} />
                              </div>
                              <p className="text-gray-700 text-sm sm:text-base">{shift}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      <div className="mt-4 md:mt-0">
                        <h3 className="font-bold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
                          <Building className="w-4 h-4 sm:w-5 sm:h-5" style={{color: COLORS.primary}} />
                          <span>Business Response</span>
                        </h3>
                        <div className="space-y-2 sm:space-y-3">
                          {trend.shifts.businessResponse.map((response, idx) => (
                            <div key={idx} className="flex items-start gap-2 sm:gap-3">
                              <div className="p-1 rounded-full mt-1" style={{backgroundColor: `${COLORS.primary}15`}}>
                                <ArrowUpRight className="w-3 h-3 sm:w-4 sm:h-4" style={{color: COLORS.primary}} />
                              </div>
                              <p className="text-gray-700 text-sm sm:text-base">{response}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            )}
            
            {/* Examples Section if available */}
            {trend.examples && (
              <section ref={sectionRefs.examples} className="scroll-mt-24">
                <div className="space-y-6 sm:space-y-8">
                  <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-4 sm:mb-6">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-white" style={{backgroundColor: COLORS.primary}}>
                        <Briefcase className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                      </div>
                      <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Market Examples</h2>
                    </div>
                    
                    <div className="mb-6 sm:mb-8">
                      <h3 className="font-bold text-gray-900 mb-3 sm:mb-4 text-base sm:text-lg">Startup Examples</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                        {trend.examples.startups.map((example, idx) => (
                          <ExampleCard key={idx} example={example} type="startup" />
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="font-bold text-gray-900 mb-3 sm:mb-4 text-base sm:text-lg">Corporate Examples</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                        {trend.examples.corporates.map((example, idx) => (
                          <ExampleCard key={idx} example={example} type="corporate" />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            )}
            
            {/* Scenarios Section if available */}
            {trend.futureScenarios && (
              <section ref={sectionRefs.scenarios} className="scroll-mt-24">
                <div className="space-y-6 sm:space-y-8">
                  <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-4 sm:mb-6">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-white" style={{backgroundColor: COLORS.primary}}>
                        <Compass className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                      </div>
                      <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Future Scenarios</h2>
                    </div>
                    <p className="text-gray-600 text-sm sm:text-base mb-4 sm:mb-6">Exploring possible futures based on how key drivers might evolve:</p>
                    <div className="space-y-4 sm:space-y-6">
                      {trend.futureScenarios.map((scenario, idx) => (
                        <ScenarioCard key={idx} scenario={scenario} index={idx} />
                      ))}
                    </div>
                  </div>
                </div>
              </section>
            )}
            
            {/* Implications Section if available */}
            {trend.strategicImplications && (
              <section ref={sectionRefs.implications} className="scroll-mt-24">
                <div className="space-y-6 sm:space-y-8">
                  <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 mb-6 sm:mb-8 shadow-sm">
                    <div className="flex items-center gap-3 mb-4 sm:mb-6">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-white" style={{backgroundColor: COLORS.primary}}>
                        <Target className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                      </div>
                      <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Strategic Implications</h2>
                    </div>
                    
                    <div className="space-y-3 sm:space-y-4">
                      {trend.strategicImplications.map((implication, idx) => (
                        <div key={idx} className="flex items-start gap-2 sm:gap-3 p-3 sm:p-4 rounded-lg border" style={{backgroundColor: `${COLORS.primary}10`, borderColor: `${COLORS.primary}30`}}>
                          <div className="p-2 rounded-full mt-1 flex items-center justify-center" style={{backgroundColor: `${COLORS.primary}20`}}>
                            <Target className="w-3 h-3 sm:w-4 sm:h-4" style={{color: COLORS.primary}} />
                          </div>
                          <p className="text-gray-700 text-sm sm:text-base">{implication}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {trend.questionsForTeams && (
                    <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 mb-6 sm:mb-8 shadow-sm">
                      <h3 className="font-bold text-gray-900 mb-4 sm:mb-6 text-lg sm:text-xl">Questions For Teams</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                        {trend.questionsForTeams.map((question, idx) => (
                          <div key={idx} className="flex items-start gap-2 sm:gap-3 p-3 sm:p-4 rounded-lg border" style={{backgroundColor: `${COLORS.primary}10`, borderColor: `${COLORS.primary}30`}}>
                            <div className="p-1 bg-white rounded-full mt-1 flex items-center justify-center shadow-sm">
                              <MessageSquare className="w-3 h-3 sm:w-4 sm:h-4" style={{color: COLORS.primary}} />
                            </div>
                            <p className="text-gray-700 text-sm sm:text-base">{question}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {trend.nextSteps && (
                    <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 shadow-sm">
                      <h3 className="font-bold text-gray-900 mb-4 sm:mb-6 text-lg sm:text-xl">Suggested Next Steps</h3>
                      <div className="space-y-3 sm:space-y-4">
                        {trend.nextSteps.map((step, idx) => (
                          <div key={idx} className="flex items-start gap-2 sm:gap-3">
                            <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center shadow-sm text-white" style={{backgroundColor: COLORS.primary}}>
                              <span className="font-bold text-xs sm:text-base">{idx + 1}</span>
                            </div>
                            <div className="py-1">
                              <p className="text-gray-700 text-sm sm:text-base">{step}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </section>
            )}
            
         
          </div>
          
          {/* Sidebar */}
          <div className="space-y-6">
            <div className="hidden lg:block lg:sticky lg:top-32">
              <LeadForm trendTitle={trend.title} color={trend.color} />
              
              <div className="mt-6 bg-white rounded-xl border border-gray-200 p-4 sm:p-6 shadow-sm">
  <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
    <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-white" style={{backgroundColor: COLORS.primary}}>
      <MessageSquare className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
    </div>
    <h3 className="font-bold text-gray-900 text-sm sm:text-base">Speak With an Analyst</h3>
  </div>
  <p className="text-gray-600 text-xs sm:text-sm mb-4 sm:mb-6">
    Schedule a call with one of our innovation experts to discuss how this opportunity applies to your business.
  </p>
  <a 
    href="https://calendly.com/lusidea/chat-with-lusidea" 
    target="_blank" 
    rel="noopener noreferrer"
    className="w-full py-2 sm:py-2.5 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors flex items-center justify-center gap-2 text-sm"
  >
    <Briefcase className="w-3 h-3 sm:w-4 sm:h-4" />
    <span>Schedule a Call</span>
  </a>
</div>
              
              <div className="mt-6 bg-white rounded-xl p-4 sm:p-6 border border-gray-200">
                <div className="flex items-center gap-2 mb-3 sm:mb-4">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center border" style={{backgroundColor: `${COLORS.primary}10`, borderColor: `${COLORS.primary}30`}}>
                    <Lightbulb className="w-2.5 h-2.5 sm:w-3 sm:h-3" style={{color: COLORS.primary}} />
                  </div>
                  <h3 className="font-medium text-black-900 text-xs sm:text-sm">
                    {searchQuery ? 'Search Results' : 'Related Trends'}
                  </h3>
                </div>
                <ul className="space-y-1 sm:space-y-2 text-xs sm:text-sm">
                  {relatedTrends
                    .slice(0, 5)
                    .map(t => (
                      <li key={t.id} className="group">
                        <Link 
                          to={getTrendUrl(t)}
                          className="flex items-center gap-1 sm:gap-2 p-2 rounded-lg hover:bg-gray-50 transition-colors group-hover:text-red-600"
                        >
                          <ArrowRight className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-gray-400 group-hover:text-red-600" />
                          <div>
                            <span className="block line-clamp-1">{t.title}</span>
                            <span className="text-xs text-gray-500">{t.category}</span>
                          </div>
                        </Link>
                      </li>
                    ))
                  }
                  {searchQuery && relatedTrends.length > 5 && (
                    <li className="pt-2 text-center">
                      <Link
                        to="/"
                        className="inline-block text-xs sm:text-sm font-medium px-2 sm:px-3 py-1 rounded-lg hover:bg-gray-50"
                        style={{ color: COLORS.primary }}
                      >
                        See all results
                      </Link>
                    </li>
                  )}
                </ul>
              </div>
              
              <div className="mt-6">
                <div className="rounded-xl p-4 sm:p-6 shadow-md text-white" style={{backgroundColor: COLORS.primary}}>
                  <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/30">
                      <Download className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                    </div>
                    <h3 className="font-bold text-white text-base sm:text-lg">Get Full Analysis</h3>
                  </div>
                  <p className="text-white/90 mb-4 sm:mb-5 text-xs sm:text-sm">
                    Request our comprehensive innovation report with detailed strategies and opportunities tailored to your industry.
                  </p>
                  <button className="w-full py-2 sm:py-3 bg-white text-gray-900 rounded-lg hover:bg-gray-100 transition-colors shadow-md flex items-center justify-center gap-2 font-medium text-sm">
                    <Download className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span>Request Full Report</span>
                  </button>
                </div>
              </div>
            </div>
            
            {/* Mobile Sidebar Bottom Section */}
            <div className="lg:hidden mt-8 space-y-6">
              <LeadForm trendTitle={trend.title} color={trend.color} />
              
              <div className="rounded-xl p-4 shadow-md text-white" style={{backgroundColor: COLORS.primary}}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/30">
                    <MessageSquare className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-bold text-white text-lg">Need Help?</h3>
                </div>
                <p className="text-white/90 mb-4 text-sm">
                  Speak with one of our innovation experts about how this trend applies to your specific business challenges.
                </p>
                <button className="w-full py-2.5 bg-white text-gray-900 rounded-lg hover:bg-gray-100 transition-colors shadow-md flex items-center justify-center gap-2 font-medium">
                  <Briefcase className="w-4 h-4" />
                  <span>Schedule a Call</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Mobile Action buttons that stay at the bottom of the screen */}
      <div className="fixed bottom-0 left-0 right-0 lg:hidden bg-white border-t border-gray-200 px-4 py-3 z-40">
        <div className="flex justify-between gap-3">
          <button
            className="flex-1 py-2 px-3 bg-white border border-gray-200 text-gray-700 rounded-lg hover:border-red-300 transition-colors text-sm font-medium flex items-center justify-center gap-1"
            onClick={handleShareTrend}
          >
            <Share2 className="w-4 h-4" />
            <span>Share</span>
          </button>
          <button
            className="flex-1 py-2 px-3 text-white rounded-lg transition-colors text-sm font-medium flex items-center justify-center gap-1"
            style={{ backgroundColor: COLORS.primary }}
          >
            <Download className="w-4 h-4" />
            <span>Get Report</span>
          </button>
        </div>
      </div>
      
      {/* Bottom padding to account for fixed action buttons */}
      <div className="pb-16 lg:pb-0"></div>
      
      <Footer />
    </div>
  );
};

export default TrendDetailPage;